import type { ApiErrorBody } from '@oms/contracts';

/** A field-level validation message as returned by the API in `error.details` for VALIDATION_FAILED. */
export interface FieldIssue {
  path: string;
  message: string;
}

/** Error raised for every non-2xx response (and for network failures, with code NETWORK_ERROR). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(init: { code: string; message: string; status: number; details?: unknown; requestId?: string | undefined }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** Validation issues in `{path, message}` form; empty when the error carries none. */
  get fieldIssues(): FieldIssue[] {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is FieldIssue => typeof d === 'object' && d !== null && typeof (d as FieldIssue).path === 'string' && typeof (d as FieldIssue).message === 'string',
    );
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/** Base URL of the API (no trailing slash). Empty means same origin (the dev server proxies /api). */
export const API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
export const API_PREFIX = '/api/v1';

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {
  const { pathname, search } = window.location;
  const isAdmin = pathname.startsWith('/admin');
  const target = isAdmin ? '/admin/login' : '/login';
  if (pathname === target) return;
  window.location.assign(`${target}?next=${encodeURIComponent(pathname + search)}`);
};

/** Replace the 401 handler (the app installs one that uses the router and clears cached data). */
export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  /** JSON body (serialised) or FormData (sent as multipart). */
  body?: unknown;
  signal?: AbortSignal;
  /**
   * When true a 401 is returned to the caller as an ApiError without triggering the global
   * sign-in redirect. Used by the session probe and the public auth endpoints.
   */
  allowUnauthenticated?: boolean;
}

export function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
  }
  const q = qs.toString();
  return `${API_BASE_URL}${API_PREFIX}${path}${q ? `?${q}` : ''}`;
}

/** Convert a fetch Response into an ApiError. Exported for tests. */
export async function toApiError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    body = null;
  }
  const e = body?.error;
  const requestId = e?.requestId ?? res.headers.get('x-request-id') ?? undefined;
  if (e && typeof e.code === 'string' && typeof e.message === 'string') {
    return new ApiError({ code: e.code, message: e.message, status: res.status, details: e.details, requestId });
  }
  return new ApiError({ code: fallbackCode(res.status), message: fallbackMessage(res.status), status: res.status, requestId });
}

function fallbackCode(status: number): string {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 413) return 'PAYLOAD_TOO_LARGE';
  if (status === 429) return 'RATE_LIMITED';
  return status >= 500 ? 'INTERNAL' : 'HTTP_ERROR';
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested item was not found.';
  if (status === 413) return 'The file is too large.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) return 'The server could not complete the request. Please try again.';
  return `Request failed (${status}).`;
}

/**
 * Typed fetch wrapper. Always sends the HttpOnly session cookie (`credentials: 'include'`); the
 * web app never reads, stores or forwards the bearer token returned by login.
 */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST');
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body; // browser sets the multipart boundary
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method, headers, body, credentials: 'include', signal: opts.signal ?? null });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError({ code: 'NETWORK_ERROR', message: 'Could not reach the server. Check your connection and try again.', status: 0 });
  }

  if (!res.ok) {
    const error = await toApiError(res);
    if (res.status === 401 && !opts.allowUnauthenticated) onUnauthorized();
    throw error;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'method' | 'query' | 'body'>) => apiRequest<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => apiRequest<T>(path, { ...opts, method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => apiRequest<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => apiRequest<T>(path, { ...opts, method: 'DELETE' }),
};

/** A user-facing sentence for any thrown value. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) {
    if (e.code === 'VERSION_CONFLICT') return 'Someone else changed this record while you were editing. Reload to see the latest version, then reapply your changes.';
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
